import type { TargetRow } from "@empire/shared";
import { useEffect } from "react";
import { protectionLeft } from "../components/LootLine.js";
import { useAttackStore } from "../../store/attack.js";
import { useSessionStore } from "../../store/session.js";

/**
 * Who there is to attack (S5-5, client side).
 *
 * The Map tab's list view (the map itself is WorldMap.tsx). It only shows testers
 * who have saved a defence — attacking an empty board tests nothing, and a
 * twenty-person playtest has no attacks to spare on it.
 */
export function TargetList({ header = true }: { header?: boolean } = {}): JSX.Element {
  const targets = useAttackStore((s) => s.targets);
  const loading = useAttackStore((s) => s.loadingTargets);
  const error = useAttackStore((s) => s.targetsError);
  const load = useAttackStore((s) => s.loadTargets);
  const confirm = useAttackStore((s) => s.confirm);
  const sessionStatus = useSessionStore((s) => s.status);

  useEffect(() => {
    if (sessionStatus === "ready") void load();
  }, [sessionStatus, load]);

  return (
    <div className="flex h-full flex-col">
      {header ? (
        <header className="flex shrink-0 items-baseline justify-between border-b border-panel-edge bg-bg-raised px-4 py-3">
          <div>
            <h2 className="text-sm font-semibold text-ink">Targets</h2>
            <p className="text-[11px] text-ink-dim">
              Other players, and barbarian villages to practise on. Start with an easy one.
            </p>
          </div>
          <button
            type="button"
            onClick={() => void load()}
            disabled={loading}
            className="h-11 shrink-0 rounded-lg border border-panel-edge bg-panel px-3 text-xs font-semibold text-ink-dim enabled:hover:text-ink disabled:opacity-40"
          >
            {loading ? "…" : "Refresh"}
          </button>
        </header>
      ) : null}

      <div className="min-h-0 flex-1 overflow-y-auto p-3">
        {error ? (
          <p className="rounded-lg border border-warn/30 bg-warn/10 px-3 py-2 text-[11px] text-warn">
            {error}
          </p>
        ) : null}

        {!error && targets.length === 0 && !loading ? (
          <div className="rounded-xl border border-panel-edge bg-panel px-4 py-6 text-center">
            <p className="text-sm text-ink">Nobody to attack yet.</p>
            <p className="mt-1 text-[11px] text-ink-dim">
              A tester appears here once they save a defence. Until then, the only maze in the world
              is yours.
            </p>
          </div>
        ) : null}

        <ul className="flex flex-col gap-2">
          {ordered(targets).map((target) => (
            <li key={target.villageId}>
              <button
                type="button"
                data-testid={`target-${target.villageId}`}
                disabled={
                  protectionLeft(target.attackBlockedUntil ?? target.protectedUntil) !== null
                }
                onClick={() => {
                  // Same tab: choosing a target opens the planner in place.
                  confirm(target);
                }}
                className="w-full rounded-xl border border-panel-edge bg-panel px-3 py-3 text-left enabled:hover:border-gold disabled:opacity-60"
              >
                <span className="flex items-baseline justify-between gap-3">
                  <span className="truncate text-sm font-semibold text-ink">
                    {target.villageName}
                  </span>
                  {target.difficulty ? (
                    <span
                      data-testid={`difficulty-${target.difficulty}`}
                      className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${DIFFICULTY_STYLE[target.difficulty]}`}
                    >
                      {target.difficulty}
                    </span>
                  ) : (
                    <span className="shrink-0 text-[10px] tabular-nums text-ink-dim">
                      ({target.x}, {target.y})
                    </span>
                  )}
                </span>
                <span className="mt-0.5 block truncate text-[11px] text-ink-dim">
                  {target.playerName}
                </span>
                <span className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[10px] tabular-nums text-ink-dim">
                  <span>
                    attacked {target.attackedCount} {target.attackedCount === 1 ? "time" : "times"}
                  </span>
                  {target.breachedByMe ? (
                    <span className="font-bold uppercase text-ok">you broke this</span>
                  ) : null}
                  {protectionLeft(target.attackBlockedUntil ?? target.protectedUntil) ? (
                    // D-118: a new player cannot be attacked yet.
                    <span className="font-bold uppercase text-gold" data-testid="target-protected">
                      {target.attackBlockReason ?? "Protected"} ·{" "}
                      {protectionLeft(target.attackBlockedUntil ?? target.protectedUntil)}
                    </span>
                  ) : null}
                  {target.layoutChangedAt ? (
                    <span className="opacity-70">changed {ago(target.layoutChangedAt)}</span>
                  ) : null}
                </span>
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

/**
 * Barbarian villages (D-113) wear their difficulty, so a new player can pick a
 * fight their ten Militia can win instead of guessing.
 */
export const DIFFICULTY_STYLE: Record<NonNullable<TargetRow["difficulty"]>, string> = {
  easy: "bg-ok/15 text-ok",
  medium: "bg-gold/15 text-gold",
  hard: "bg-warn/15 text-warn",
  brutal: "bg-danger/15 text-danger",
};

const RANK = { easy: 0, medium: 1, hard: 2, brutal: 3 } as const;

/**
 * Barbarians first, easiest on top — the first step sends a new player at the
 * Outpost, and it must be the first thing they see — then real players.
 */
function ordered(targets: readonly TargetRow[]): TargetRow[] {
  const players = targets.filter((t) => !t.difficulty);
  const barbarians = targets
    .filter((t) => t.difficulty)
    .sort(
      (a, b) =>
        RANK[a.difficulty!] - RANK[b.difficulty!] ||
        (a.progressionTier ?? 0) - (b.progressionTier ?? 0)
    );
  return [...barbarians, ...players];
}

/** Rough, and deliberately so — the exact minute of a layout change is noise. */
function ago(iso: string): string {
  const seconds = Math.max(0, (Date.now() - Date.parse(iso)) / 1000);
  if (seconds < 90) return "just now";
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 36) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
}
